import { useCallback, useEffect, useRef, useState } from 'react';

import {
  btag,
  Bus,
  constants,
  NotificationManager,
  PaymentV2Api,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
  usePaymentMethods,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';

import {
  ACCOUNT_STATUSES,
  bonusStatuses,
  DATA_LAYER_STORAGE_KEYS,
  POSTHOG_CTA,
} from 'src/common/constants';
import {
  buildPaymentUrl,
  showAlternativeMethodsOrError,
} from 'src/common/Notifications/Popups/alternativePaymentUtils';
import { useBonusesContext } from 'src/components/BonusesProvider';
import { getMinDeposit, isAmountBelowMinDeposit } from 'src/components/BonusesProvider/utils';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { depositSubmit } from 'src/helpers/depositSubmit';

export const useDepositSubmit = ({
  browser,
  closeQRCodeOnSuccess,
  history,
  ip,
  locale,
  onClose,
  onDepositStart,
  onDepositSuccess,
  os,
  paymentMethod,
  setRedirectUrl,
  showInPopup,
  value,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [depositPopupId, setDepositPopupId] = useState(null);
  const loadingNotificationId = useRef();
  const { activatedBonus, selectedBonus } = useBonusesContext();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const {
    account,
    wallet: { currency, detailedBalance },
  } = useApplicationState();
  const { payment } = clientProfileRoutes;
  const { deposit: depositIndex } = paymentRoutes;
  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);

  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.DESKTOP,
  );

  const { BONUS = 0, CASH = 0, FROZEN = 0 } = detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;

  const onCreateDeposit = useCallback(() => {
    const notificationId = NotificationManager.display({
      data: {
        headerTitle: 'deposit_loading',
        text: 'deposit_loading_text',
        title: 'deposit_loading_title',
      },
      type: 'paymentLoading',
    });
    loadingNotificationId.current = notificationId;

    return notificationId;
  }, []);

  const onDepositStatusError = useCallback(() => {
    Bus.emit('ui:depositErrorHandled');

    NotificationManager.removeNotification(loadingNotificationId.current);

    const fallbackError = () => {
      NotificationManager.display({
        data: {
          headerTitle: 'deposit',
          text: 'deposit_failed_message',
          title: 'deposit_failed_title',
          withHeader: false,
        },
        type: 'error',
      });
    };

    if (!paymentMethod?.alternativePmEnabled) {
      fallbackError();
      return;
    }

    const depositPageLink = `${clientProfilePath}/${payment}/${depositIndex}`;

    showAlternativeMethodsOrError({
      fallbackError,
      onSelectMethod: method =>
        history.push(buildPaymentUrl(`${depositPageLink}/${method.id}`, value, method)),
      onViewAll: () =>
        history.push(buildPaymentUrl(`${depositPageLink}/${paymentMethods[0].id}`, value, null)),
      operationType: 'DEPOSIT',
      pmId: paymentMethod?.id,
    });
  }, [clientProfilePath, depositIndex, history, payment, paymentMethod, paymentMethods, value]);

  useEffect(() => {
    if (!depositPopupId || !closeQRCodeOnSuccess) return;

    if (balance) {
      const timeoutId = setTimeout(() => {
        NotificationManager.removeNotification(depositPopupId);
        setDepositPopupId(null);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [balance, closeQRCodeOnSuccess, depositPopupId]);

  const handleDeposit = useCallback(async () => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      history.push(showInPopup ? '?cmd=signup' : staticRoutes.signup);
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    const submitBonus = selectedBonus || activatedBonus;

    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED)) {
      pushToDataLayer(
        'qd_deposit_button_click',
        {
          OS: os,
          amount: value,
          bonus_toggle_enabled: !!submitBonus,
          browser,
          created_at: Date.now(),
          device: constants.DEVICE_TYPES.DESKTOP,
          ip,
          locale,
          page_referer: window.location.pathname,
          payment_method: paymentMethod?.name,
          user_id: account.id,
        },
        btag,
      );
      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED, true);
    }

    const isBonusInstance = submitBonus?.status === bonusStatuses.ACTIVATED;
    const resolvedBonusId = isBonusInstance ? submitBonus?.bonus?.id : submitBonus?.id;
    const shouldSendBonus =
      !!submitBonus &&
      !submitBonus.disabled &&
      !isAmountBelowMinDeposit(value, getMinDeposit(submitBonus, currency));
    const bonusId = shouldSendBonus ? resolvedBonusId : null;

    const options = {
      amount: value,
      currency,
      pmId: paymentMethod.id,
      ...(shouldSendBonus && {
        additionalAttributes: {
          bonusId: resolvedBonusId,
          bonusInstanceId: isBonusInstance ? submitBonus.id : null,
        },
      }),
    };

    onDepositStart?.({
      amount: value,
      bonus_id: bonusId,
      cta_clicked: POSTHOG_CTA.DEPOSIT,
      payment_method: paymentMethod?.name ?? null,
    });

    setLoading(true);

    PaymentV2Api.getPaymentData({
      onCreatePayment: onCreateDeposit,
      onPaymentStatusError: onDepositStatusError,
      paymentOptions: options,
      paymentType: PaymentV2Api.PaymentType.DEPOSIT,
    })
      .then(data => {
        NotificationManager.removeNotification(loadingNotificationId.current);

        pushToDataLayer(
          'deposit_created',
          {
            amount: value,
            client: { id: account.id },
            created_at: Date.now(),
            currency,
            payment_method: data.paymentName,
          },
          btag,
        );

        onDepositSuccess?.({
          transaction_id: data?.publicPaymentId ?? data?.paymentId ?? null,
        });

        const notificationId = depositSubmit({
          amount: value,
          data,
          paymentMethod,
          paymentOptions: options,
          setRedirectUrl,
        });

        setDepositPopupId(notificationId);
        onClose?.();
      })
      .catch(err => {
        if (err?.code) setError({ message: err.code });
      })
      .finally(() => setLoading(false));
  }, [
    account.id,
    account.status,
    activatedBonus,
    browser,
    currency,
    history,
    ip,
    locale,
    onClose,
    onCreateDeposit,
    onDepositStart,
    onDepositStatusError,
    onDepositSuccess,
    os,
    paymentMethod,
    selectedBonus,
    setRedirectUrl,
    showInPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopupIfNeeded,
    value,
  ]);

  return {
    error,
    handleDeposit,
    loading,
    setError,
  };
};
